import React from 'react';
import { Box, Container, Typography, Chip, Grid, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

type Status = 'shipped' | 'in-progress' | 'planned';

interface RoadmapItem {
  title: string;
  description: string;
  status: Status;
  eta?: string;
}

interface Quarter {
  label: string;
  items: RoadmapItem[];
}

const quarters: Quarter[] = [
  {
    label: 'Q1 2026 — Shipped',
    items: [
      { title: 'Razorpay payment integration', description: 'INR billing via Razorpay for Indian customers alongside Stripe.', status: 'shipped' },
      { title: 'False positive management', description: 'Mark scan findings as accepted risk with justification and reviewer workflow.', status: 'shipped' },
      { title: 'Analytics dashboard', description: 'Scan trend charts, risk score history, and per-framework compliance breakdown.', status: 'shipped' },
      { title: 'PDF & executive summary export', description: 'Audit-ready PDF reports with HIPAA clause mapping.', status: 'shipped' },
      { title: 'GitHub Actions integration', description: 'Official workflow step for automated compliance scanning in CI.', status: 'shipped' },
    ],
  },
  {
    label: 'Q2 2026 — In Progress',
    items: [
      { title: 'Custom rule authoring', description: 'Write and deploy your own compliance rules via YAML — no code required.', status: 'in-progress', eta: 'May 2026' },
      { title: 'Slack & Teams notifications', description: 'Send scan failure alerts directly to your team channels.', status: 'in-progress', eta: 'May 2026' },
      { title: 'SOC 2 Type II evidence package', description: 'One-click evidence export formatted for SOC 2 Type II audits.', status: 'in-progress', eta: 'June 2026' },
      { title: 'Azure DevOps pipeline plugin', description: 'Native Azure DevOps task for compliance scanning in pipelines.', status: 'planned', eta: 'June 2026' },
    ],
  },
  {
    label: 'Q3 2026 — Planned',
    items: [
      { title: 'Team collaboration', description: 'Invite teammates, assign roles (Admin/Developer/Auditor), and share scan access.', status: 'planned', eta: 'Q3 2026' },
      { title: 'API access', description: 'Programmatic REST API to trigger scans, fetch results, and manage tokens.', status: 'planned', eta: 'Q3 2026' },
      { title: 'HITRUST r2 Certification mapping', description: 'Map scan results directly to HITRUST r2 certification requirements.', status: 'planned', eta: 'Q3 2026' },
      { title: 'Scheduled scans', description: 'Schedule recurring scans on a cron — get alerted when posture degrades.', status: 'planned', eta: 'Q3 2026' },
      { title: 'Kubernetes manifest scanning', description: 'Scan Kubernetes YAML manifests for healthcare-relevant misconfigurations.', status: 'planned', eta: 'Q3 2026' },
    ],
  },
  {
    label: 'Q4 2026 — Exploring',
    items: [
      { title: 'AI-powered remediation suggestions', description: 'Get fix suggestions for each finding, generated from the rule context and your config.', status: 'planned' },
      { title: 'VS Code extension', description: 'Real-time compliance hints in your editor as you write IaC and config files.', status: 'planned' },
      { title: 'SSO / SAML', description: 'Enterprise SSO via SAML 2.0 and OIDC for large organizations.', status: 'planned' },
      { title: 'Compliance posture score', description: 'A single composite score across all frameworks with benchmark comparisons.', status: 'planned' },
    ],
  },
];

const statusConfig: Record<Status, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
  shipped:     { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, label: 'Shipped',     color: '#16a34a', bg: 'rgba(22,163,74,0.1)' },
  'in-progress': { icon: <BuildCircleIcon sx={{ fontSize: 18 }} />, label: 'In Progress', color: PRIMARY,    bg: 'rgba(255,117,24,0.1)' },
  planned:     { icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />, label: 'Planned', color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
};

export function Roadmap() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box component="a" href="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>BaaStream</Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip label="Roadmap" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }} />
          <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            Where we're{' '}
            <Box component="span" sx={{ color: PRIMARY }}>headed</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            Our public roadmap — what we've shipped, what we're building, and what's coming next.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 2 }}>
            Have a feature request?{' '}
            <Box component="a" href="mailto:connect@baastream.com" sx={{ color: PRIMARY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Write to us
            </Box>
          </Typography>
        </Container>
      </Box>

      {/* Legend */}
      <Container maxWidth="lg" sx={{ pt: 6 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>
              <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>{cfg.label}</Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Quarters */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {quarters.map((quarter, qi) => (
          <Box key={quarter.label}>
            <Typography variant="overline" sx={{ color: DARK, fontWeight: 700, letterSpacing: 1.5, fontSize: '0.75rem' }}>
              {quarter.label}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              {quarter.items.map((item) => {
                const cfg = statusConfig[item.status];
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
                    <Box
                      sx={{
                        p: 2.5,
                        border: '1px solid',
                        borderColor: item.status === 'shipped' ? 'rgba(22,163,74,0.2)' : item.status === 'in-progress' ? 'rgba(255,117,24,0.2)' : 'rgba(0,0,0,0.08)',
                        borderRadius: 2,
                        height: '100%',
                        bgcolor: item.status === 'shipped' ? 'rgba(22,163,74,0.02)' : '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Chip
                          icon={<Box sx={{ color: cfg.color, display: 'flex', ml: '4px !important' }}>{cfg.icon}</Box>}
                          label={cfg.label}
                          size="small"
                          sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.72rem' }}
                        />
                        {item.eta && (
                          <Typography variant="caption" sx={{ color: '#999' }}>{item.eta}</Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: DARK, mb: 0.75 }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>{item.description}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            {qi < quarters.length - 1 && <Divider sx={{ my: 5 }} />}
          </Box>
        ))}

        {/* CTA */}
        <Box sx={{ mt: 8, p: 4, bgcolor: 'rgba(255,117,24,0.04)', border: '1px solid rgba(255,117,24,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>Shape the roadmap</Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            We prioritise based on customer feedback. If something's missing, tell us — we read every email.
          </Typography>
          <Box
            component="a"
            href="mailto:connect@baastream.com"
            sx={{ display: 'inline-block', px: 4, py: 1.5, bgcolor: PRIMARY, color: '#fff', borderRadius: 2, fontWeight: 600, textDecoration: 'none', '&:hover': { opacity: 0.9 } }}
          >
            Send feedback
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} Monks Do Work. All rights reserved.</Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>← Back to Home</Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
